import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Button, MenuItem } from '@mui/material';
import { toast } from 'react-toastify';
import { ShimmerTable } from 'react-shimmer-effects';
import ArticleIcon from '@mui/icons-material/Article';
import YouTubeIcon from '@mui/icons-material/YouTube';
import AddIcon from '@mui/icons-material/Add';
import SPMetadataModal from '../../components/CASBApp/AuthenticationSource/SPMetadataModal';
import { RootState, AppDispatch } from '../../Redux/store';
import MainLayout from '../../components/MainLayout';
import {
  cyanBlueBackgroundButtonStyles,
  lightBackgroundButtonStyles,
} from '../../styles/button';
import { StyledSelect, menuItemCSS } from '../../styles/select';
import {
  fetchAllAuthentications,
  addNewAuthenticationSource,
  updateAuthenticationSource,
  updateOpenDrawer,
} from '../../Redux/Slices/Application/AuthenticationSource/authenticationSourceSlice';
import {
  deleteAuthenticationSource,
  deleteBulkAuthenticationSource,
} from '../../Redux/Slices/Application/AuthenticationSource/authenticationSourceHandler';
import { SubTitle } from '../../styles/subtitle';
import { buttonIconStyles } from '../../styles/icon';
import { ResponsiveHeading } from '../../styles/heading';
import { SearchBar } from '../../styles/textField';
import AuthenticationSourceConfiguration from './AddAuthenticationSource';
import { getUser, testSAMLConnection } from '../../utils/utility';
import { handleSearch } from '../../utils/Groups/groupUtility';
import CommonTable from '../../components/Table/CommonTable';
import GuideButton from '../../components/Buttons/GuideButtons';
import DeleteModal from '../../components/modal/deleteModal';
import { disabledButtonStyles } from '../../styles/disabledButtonStyles';

function AuthenticationSourceView() {
  const dispatch = useDispatch<AppDispatch>();
  const [viewMetadata, setViewMetadata] = useState(false);
  const [bulkAction, setBulkAction] = useState('none');
  const { currentActiveTenantName } = useSelector(
    (state: RootState) => state.customer,
  );
  const { configuredAuthenticationSource, status } = useSelector(
    (state: RootState) => state.authenticationSource,
  );
  const { spAcsURL } = useSelector(
    (state: RootState) => state.authenticationSource,
  );
  const [selectedAuthenticationIds, setSelectedAuthenticationIds] = useState<
    string[]
  >([]);
  const [openDialog, setOpenDialog] = useState(false);

  const [
    filteredAuthenticationSource,
    setFilteredAuthenticationSource,
  ] = useState(configuredAuthenticationSource);

  useEffect(() => {
    setFilteredAuthenticationSource(configuredAuthenticationSource);
  }, [configuredAuthenticationSource]);

  const handleSelectedRowsChange = (newSelectedRows: string[]) => {
    setSelectedAuthenticationIds(newSelectedRows);
  };

  useEffect(() => {
    if (currentActiveTenantName !== '') {
      dispatch(fetchAllAuthentications());
    }
  }, [currentActiveTenantName]);

  const handleEditAuthenticationSource = (id: any) => {
    configuredAuthenticationSource.forEach(async (element: any) => {
      if (element._id === id) {
        dispatch(updateAuthenticationSource(element));
        dispatch(updateOpenDrawer(true));
      }
    });
  };

  const configureNewAuthenticationSource = async () => {
    dispatch(addNewAuthenticationSource());
    dispatch(updateOpenDrawer(true));
  };

  const handleViewMetadata = (id: string) => {
    configuredAuthenticationSource.forEach(async (element: any) => {
      if (element._id === id) {
        await dispatch(updateAuthenticationSource(element));
        setViewMetadata(true);
      }
    });
  };

  const deleteAuthenticationSourceHandler = (id: string) => {
    dispatch(deleteAuthenticationSource(id));
  };
  const copyMetadataURL = async (id: string) => {
    const user = await getUser();
    const url = `${process.env.REACT_APP_NODE_BACKEND_URL}/backend/${user.customer_id}/${user?.current_active_tenant_id}/${id}/generate-sp-metadata`;
    navigator.clipboard.writeText(url);
    toast.success('Copied Metadata URL');
  };

  const handleTestConnection = async (id: string) => {
    testSAMLConnection(id, spAcsURL);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleConfirmRevoke = async () => {
    if (bulkAction === 'delete') {
      await dispatch(deleteBulkAuthenticationSource(selectedAuthenticationIds));
    }
    handleCloseDialog();
  };

  const styles = {
    height: { xxl: '33.438rem', xl: '22.188rem', md: '18.75rem' },
  };
  const columnsToSearch = ['identifier', 'authentication_type'];

  return (
    <MainLayout>
      <Box
        p={{ md: '0.5rem', lg: '2rem', xl: '2rem' }}
        sx={{ overflowY: 'scroll', height: '95vh' }}
      >
        <SPMetadataModal open={viewMetadata} setOpen={setViewMetadata} />
        <AuthenticationSourceConfiguration />
        <Box display="flex" flexDirection="column" gap="0.5rem">
          <ResponsiveHeading>Authentication Source</ResponsiveHeading>
          <SubTitle>
            Configure authentication source for your applications here.
          </SubTitle>
        </Box>
        <Box
          mt={{ md: '0.5rem', lg: '2.5rem', xl: '2.5rem' }}
          display="flex"
          width="100%"
        >
          <Box
            display="flex"
            flexDirection="column"
            p="1.5rem"
            border="0.063rem solid #E3E3E3"
            borderRadius="0.625rem"
            width="70%"
            height="auto"
          >
            <Box display="flex" justifyContent="space-between" mb={2}>
              <SearchBar
                placeholder="Search for authentication details"
                onChange={(event: any) => handleSearch(
                  event,
                  configuredAuthenticationSource,
                  setFilteredAuthenticationSource,
                  columnsToSearch,
                )}
                sx={{
                  maxWidth: {
                    xxl: '25rem',
                    xl: '20rem',
                  },
                }}
              />
              <Box display="flex" gap="1rem" alignItems="center">
                <StyledSelect
                  sx={{
                    width: { xl: '9rem', md: '6rem' },
                    border: '2px solid #9c9c9c',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none !important',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: 'none !important',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: 'none !important',
                    },
                  }}
                  value={bulkAction}
                  onChange={(event: any) => setBulkAction(event?.target.value)}
                >
                  <MenuItem value="none" sx={menuItemCSS}>
                    Bulk Action
                  </MenuItem>
                  <MenuItem value="delete" sx={menuItemCSS}>
                    Delete
                  </MenuItem>
                </StyledSelect>
                <Button
                  sx={{
                    ...lightBackgroundButtonStyles,
                    width: { xl: '6rem', md: '4rem' },
                    border: '2px solid #9c9c9c',
                    borderRadius: '4px',
                    color: '#333',
                    backgroundColor: '#f5f5f5',
                    '&:hover': {
                      border: '2px solid #9c9c9c',
                      backgroundColor: '#e0e0e0',
                    },
                    ...disabledButtonStyles,
                  }}
                  onClick={handleOpenDialog}
                  disabled={
                    bulkAction === 'none'
                    || selectedAuthenticationIds.length === 0
                  }
                >
                  Apply
                </Button>

                <Button
                  sx={{
                    ...cyanBlueBackgroundButtonStyles,
                    width: { xl: '8rem', md: '6rem' },
                    height: { md: '2.5rem' },
                  }}
                  onClick={configureNewAuthenticationSource}
                >
                  <AddIcon sx={{ ...buttonIconStyles, color: 'white' }} />
                  &nbsp; Add New
                </Button>
              </Box>
            </Box>
            {status === 'idle' && <ShimmerTable row={5} col={4} />}
            {status === 'succeeded' && (
              <CommonTable
                data={filteredAuthenticationSource}
                columns={[
                  { field: 'identifier', headerName: 'Identifiers' },
                  {
                    field: 'authentication_type',
                    headerName: 'Authentication Type',
                  },
                ]}
                styles={styles}
                statusColumn={{
                  field: 'authentication_type',
                  header: 'Authentication Type',
                }}
                pages="Authentication"
                onEdit={handleEditAuthenticationSource}
                onDelete={deleteAuthenticationSourceHandler}
                copyMetadataURL={copyMetadataURL}
                viewMetadata={handleViewMetadata}
                onSelectedRowsChange={handleSelectedRowsChange}
                testConnection={handleTestConnection}
              />
            )}
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            width="30%"
            py="1.5rem"
            px={{ md: '1rem', lg: '2.5rem', xl: '2.5rem' }}
            gap="1rem"
          >
            <GuideButton
              links={[
                'https://www.youtube.com/watch?v=E8xmwuHW2Dc&t=47s',
                'https://www.miniorange.com/reverse-proxy/setup-saml-authentication-with-reverse-proxy',
              ]}
              icons={[
                <YouTubeIcon sx={{ ...buttonIconStyles, color: 'red' }} />,
                <ArticleIcon sx={{ ...buttonIconStyles, color: 'blue' }} />,
              ]}
              labels={['Video Guide', 'Setup Guide']}
            />
          </Box>
        </Box>
        <DeleteModal
          open={openDialog}
          title="Confirm Multiple Authentication Source Deletion"
          message="Are you sure you want to delete the selected authentication sources ?"
          onConfirm={handleConfirmRevoke}
          onCancel={handleCloseDialog}
        />
      </Box>
    </MainLayout>
  );
}

export default AuthenticationSourceView;
